/**
 * DD_COMPONENT_CATALOG — DramaDetector component catalog for json-render
 *
 * Flat, human-readable representation of the catalog for tooling,
 * documentation, and unit tests. The full typed catalog (with Zod schemas)
 * lives in catalog.tsx (ddCatalog) + schema.ts — those are the source of truth.
 *
 * IMPORTANT: This file MUST stay aligned with schema.ts prop definitions.
 * When props diverge, schema.ts wins (it drives the actual runtime).
 *
 * Components (10 registered in ddCatalog):
 *   AnalysisCard          — Free+  — Container card for analysis sections
 *   DramaScore            — Free+  — Hero drama score display (0-10)
 *   TierBadge             — Free+  — Subscription tier badge
 *   NarrativeBlock        — Free+  — Prose narrative text block
 *   ActionButton          — Free+  — CTA action button
 *   SectionDivider        — Free+  — Visual separator between sections
 *   DramaCategoryBadge    — Free+  — Compact category badge
 *   InsightRow            — Free+  — Single insight list item
 *   PatternSpotlight      — Drama+ — Premium pattern analysis card
 *   TransformationInsight — DramaX — Premium before/after transformation card
 *
 * Sally's conceptual names mapped to registered names:
 *   ActionItem  → ActionButton (registered name in ddCatalog)
 *   CrisisAlert → NarrativeBlock[emphasis="critical"] (no separate component)
 *
 * Architecture ref: Winston (Discourse #3543/2)
 * Catalog spec ref: Sally (Discourse #3543/4)
 * Task: P1.4 [NocoDB #783] | Audit: #1158
 * Agent: Diego <diego@liftping.net>
 */

// ============================================================================
// Catalog entry shape
// ============================================================================

export interface CatalogComponentEntry {
  /** Short description for the LLM prompt */
  description: string;
  /** Subscription tier required to render this component */
  tier: "free" | "drama_plus" | "dramax";
  /** Prop definitions */
  props: Record<string, CatalogPropDef>;
}

export interface CatalogPropDef {
  type: string;
  required: boolean;
  description: string;
  default?: unknown;
  enum?: string[];
}

// ============================================================================
// DD_COMPONENT_CATALOG — 10 components (matches ddCatalog in catalog.tsx)
//
// Prop names and enums MUST match schema.ts exactly.
// schema.ts is the runtime source of truth.
// ============================================================================

export const DD_COMPONENT_CATALOG = {
  /**
   * 1. AnalysisCard — Free+
   * Primary container card for an analysis section.
   */
  AnalysisCard: {
    description:
      "Primary container card that groups related analysis content. " +
      'variant="standard" for free content, "elevated" for emphasis, "premium" for DramaX (gold border).',
    tier: "free",
    props: {
      title: {
        type: "string",
        required: true,
        description: "Card heading (max 60 chars)",
      },
      subtitle: {
        type: "string",
        required: false,
        description: "Optional sub-heading (max 80 chars)",
      },
      variant: {
        type: "enum",
        required: true,
        description: "Visual variant",
        enum: ["standard", "elevated", "premium"],
        default: "standard",
      },
      tierHint: {
        type: "enum",
        required: false,
        description: "Tier that unlocked this card (for TierBadge attribution)",
        enum: ["free", "drama_plus", "dramax"],
      },
    },
  } satisfies CatalogComponentEntry,

  /**
   * 2. DramaScore — Free+
   * Hero element. Always the first visible element in a result view.
   * Score: 0-10. Category drives color coding.
   */
  DramaScore: {
    description:
      "Hero element displaying the numeric drama score (0-10) with tier-appropriate color coding. " +
      "Always place this as the first visible element. " +
      'Use size="lg" for high_drama or extreme_drama categories.',
    tier: "free",
    props: {
      score: {
        type: "number",
        required: true,
        description: "Drama score from 0 (safe) to 10 (extreme drama)",
      },
      category: {
        type: "enum",
        required: true,
        description: "Drama category (drives color coding)",
        enum: ["safe", "caution", "high_drama", "extreme_drama"],
      },
      size: {
        type: "enum",
        required: false,
        description: "Display size",
        enum: ["sm", "md", "lg"],
        default: "md",
      },
      showLabel: {
        type: "boolean",
        required: false,
        description: "Show category label below score",
        default: true,
      },
    },
  } satisfies CatalogComponentEntry,

  /**
   * 3. TierBadge — Free+
   * Displays subscription tier badge for content attribution or upgrade hints.
   */
  TierBadge: {
    description:
      "Displays which subscription tier unlocked this content. " +
      "Use showUpgradeHint=true when content is previewed but not fully accessible.",
    tier: "free",
    props: {
      tier: {
        type: "enum",
        required: true,
        description: "The subscription tier to display",
        enum: ["free", "drama_plus", "dramax"],
      },
      label: {
        type: "string",
        required: false,
        description: "Optional custom label text",
      },
      showUpgradeHint: {
        type: "boolean",
        required: false,
        description: "Show an upgrade prompt alongside the badge",
        default: false,
      },
    },
  } satisfies CatalogComponentEntry,

  /**
   * 4. NarrativeBlock — Free+
   * Prose content block for narrative analysis text.
   * Props aligned with NarrativeBlockPropsSchema: text, emphasis.
   */
  NarrativeBlock: {
    description:
      "Prose content block for narrative analysis text. " +
      'Use emphasis="high" for key findings, "critical" for extreme_drama content. Max text: 2000 chars.',
    tier: "free",
    props: {
      text: {
        type: "string",
        required: true,
        description: "Narrative prose text (max 2000 chars)",
      },
      emphasis: {
        type: "enum",
        required: false,
        description: "Visual emphasis of the block",
        enum: ["normal", "high", "critical"],
        default: "normal",
      },
    },
  } satisfies CatalogComponentEntry,

  /**
   * 5. ActionButton — Free+
   * CTA action button wired to catalog actions.
   * Registered as ActionButton in ddCatalog (Sally's conceptual name was ActionItem).
   */
  ActionButton: {
    description:
      "CTA action button wired to catalog actions. " +
      "action must match one of: upgrade_tier, share_result, save_analysis, dismiss_insight.",
    tier: "free",
    props: {
      label: {
        type: "string",
        required: true,
        description: "Button label (max 60 chars)",
      },
      action: {
        type: "string",
        required: true,
        description:
          "Action ID: upgrade_tier | share_result | save_analysis | dismiss_insight",
      },
      tier: {
        type: "enum",
        required: false,
        description: "Subscription tier (drives border-radius styling)",
        enum: ["free", "drama_plus", "dramax"],
        default: "free",
      },
      variant: {
        type: "enum",
        required: false,
        description: "Visual variant",
        enum: ["primary", "secondary", "ghost", "upgrade"],
        default: "primary",
      },
      disabled: {
        type: "boolean",
        required: false,
        description: "Disable the button",
        default: false,
      },
    },
  } satisfies CatalogComponentEntry,

  /**
   * 6. SectionDivider — Free+
   * Visual horizontal separator between sections.
   */
  SectionDivider: {
    description:
      "Visual horizontal separator between sections. " +
      'Use variant="emphasized" between major sections, "subtle" between sub-sections.',
    tier: "free",
    props: {
      label: {
        type: "string",
        required: false,
        description: "Optional label centered in the divider (max 60 chars)",
      },
      variant: {
        type: "enum",
        required: false,
        description: "Visual variant",
        enum: ["subtle", "emphasized"],
        default: "subtle",
      },
      spacing: {
        type: "enum",
        required: false,
        description: "Vertical spacing around divider",
        enum: ["sm", "md", "lg"],
        default: "md",
      },
    },
  } satisfies CatalogComponentEntry,

  /**
   * 7. DramaCategoryBadge — Free+
   * Compact inline badge showing the drama category.
   */
  DramaCategoryBadge: {
    description:
      "Compact badge showing the drama category (safe/caution/high_drama/extreme_drama). " +
      "Use alongside DramaScore for quick at-a-glance category identification.",
    tier: "free",
    props: {
      category: {
        type: "enum",
        required: true,
        description: "Drama category",
        enum: ["safe", "caution", "high_drama", "extreme_drama"],
      },
      size: {
        type: "enum",
        required: false,
        description: "Badge size",
        enum: ["sm", "md"],
        default: "md",
      },
      showIcon: {
        type: "boolean",
        required: false,
        description: "Show category icon alongside label",
        default: true,
      },
    },
  } satisfies CatalogComponentEntry,

  /**
   * 8. InsightRow — Free+
   * Single insight item, typically within a list.
   */
  InsightRow: {
    description:
      "Single insight item within a list. Props: title (heading), description (detail text). " +
      "Optional trend (up/down/neutral) with changePercent for trend display.",
    tier: "free",
    props: {
      title: {
        type: "string",
        required: true,
        description: "Insight heading (max 100 chars)",
      },
      description: {
        type: "string",
        required: true,
        description: "Insight detail text (max 300 chars)",
      },
      trend: {
        type: "enum",
        required: false,
        description: "Trend direction indicator",
        enum: ["up", "down", "neutral"],
      },
      changePercent: {
        type: "number",
        required: false,
        description: "Numeric change percentage for trend display",
      },
    },
  } satisfies CatalogComponentEntry,

  /**
   * 9. PatternSpotlight — Drama+ only (tier-gated)
   * Premium pattern analysis card showing a detected communication pattern.
   */
  PatternSpotlight: {
    description:
      "[DRAMA+ ONLY] Premium pattern analysis card. " +
      "Only generate for tier=drama_plus or tier=dramax. " +
      "Include up to 3 example quotes. Suggest a constructive reframe via suggestion.",
    tier: "drama_plus",
    props: {
      patternName: {
        type: "string",
        required: true,
        description: "Pattern name (max 80 chars)",
      },
      description: {
        type: "string",
        required: true,
        description: "Pattern description (max 500 chars)",
      },
      severity: {
        type: "enum",
        required: true,
        description: "Pattern severity",
        enum: ["low", "medium", "high", "critical"],
      },
      occurrences: {
        type: "number",
        required: true,
        description: "Number of times pattern was detected (min 1)",
      },
      examples: {
        type: "string[]",
        required: false,
        description: "Up to 3 example quotes from the analyzed text",
      },
      suggestion: {
        type: "string",
        required: false,
        description: "Constructive reframing suggestion (max 400 chars)",
      },
    },
  } satisfies CatalogComponentEntry,

  /**
   * 10. TransformationInsight — DramaX only (tier-gated)
   * Premium editorial before/after transformation card.
   */
  TransformationInsight: {
    description:
      "[DRAMAX ONLY] Premium editorial before/after transformation card. " +
      "Only generate for tier=dramax. " +
      "before/after: max 600 chars each. rationale: max 400 chars.",
    tier: "dramax",
    props: {
      title: {
        type: "string",
        required: true,
        description: "Transformation title (max 80 chars)",
      },
      before: {
        type: "string",
        required: true,
        description: "Original dramatic phrasing (max 600 chars)",
      },
      after: {
        type: "string",
        required: true,
        description: "Transformed, de-escalated phrasing (max 600 chars)",
      },
      rationale: {
        type: "string",
        required: true,
        description: "Explanation of the transformation (max 400 chars)",
      },
      category: {
        type: "enum",
        required: true,
        description: "Drama category of the original text",
        enum: ["safe", "caution", "high_drama", "extreme_drama"],
      },
      impactScore: {
        type: "number",
        required: true,
        description: "Impact score (0-10, only shown if >= 6 per tier gate)",
      },
    },
  } satisfies CatalogComponentEntry,
} as const;

// ============================================================================
// Derived helpers
// ============================================================================

/** All component names in the catalog */
export const DD_CATALOG_COMPONENT_NAMES = Object.keys(
  DD_COMPONENT_CATALOG
) as (keyof typeof DD_COMPONENT_CATALOG)[];

/**
 * Core free-tier components from the catalog.
 *
 * Historical note: Sally's original conceptual spec used ActionItem and CrisisAlert.
 * In the registered ddCatalog: ActionItem → ActionButton, CrisisAlert → NarrativeBlock[emphasis="critical"].
 * This list now matches the actual registered component names.
 */
export const DD_CORE_COMPONENT_NAMES: (keyof typeof DD_COMPONENT_CATALOG)[] = [
  "AnalysisCard",
  "DramaScore",
  "TierBadge",
  "NarrativeBlock",
  "ActionButton",
  "SectionDivider",
];

/** Components requiring drama_plus or higher */
export const DD_PREMIUM_COMPONENT_NAMES = DD_CATALOG_COMPONENT_NAMES.filter(
  (name) =>
    DD_COMPONENT_CATALOG[name].tier === "drama_plus" ||
    DD_COMPONENT_CATALOG[name].tier === "dramax"
);
